import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { toast } from 'sonner';
import { useSiteEditor } from '@/contexts/SiteEditorContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cacheKey = (slug) => `page:${slug}`;

const readCache = (slug) => {
  try {
    return JSON.parse(localStorage.getItem(cacheKey(slug))) || null;
  } catch {
    return null;
  }
};

const writeCache = (slug, page) => {
  try {
    localStorage.setItem(cacheKey(slug), JSON.stringify(page));
  } catch {
    /* التخزين غير متاح */
  }
};

export const newBlockId = () => `b${Math.random().toString(36).slice(2, 10)}`;

/**
 * محتوى صفحة من لوحة التحكم + وضع التعديل للأدمن.
 * بيعرض النسخة المحفوظة على الجهاز فوراً، وبيحدّثها من السيرفر.
 *
 * data    → المسودة وقت التعديل، أو الصفحة المنشورة
 * update(path, valueOrFn) → تعديل أي حقل في المسودة (path زي ['content', 'sections', 0, 'title'])
 */
export const useEditablePage = (slug) => {
  const { canEdit, savePage } = useSiteEditor();
  const [page, setPage] = useState(() => readCache(slug));
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    axios
      .get(`${API}/pages/${slug}`)
      .then((res) => {
        setPage(res.data.data);
        writeCache(slug, res.data.data);
      })
      .catch(() => setFailed(true));
  }, [slug]);

  useEffect(() => {
    setPage(readCache(slug));
    setDraft(null);
    load();
  }, [slug, load]);

  const editing = draft !== null;
  const dirty = editing && !isEqual(draft, page);

  // تحذير قبل قفل التاب لو فيه تعديلات مش محفوظة
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const startEditing = useCallback(() => setDraft(cloneDeep(page)), [page]);

  const cancel = useCallback(() => {
    if (dirty && !window.confirm('في تعديلات مش محفوظة، بدك تلغيها؟')) return;
    setDraft(null);
  }, [dirty]);

  const update = useCallback((path, valueOrFn) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneDeep(current);
      const value = typeof valueOrFn === 'function' ? valueOrFn(get(current, path)) : valueOrFn;
      set(next, path, value);
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    if (!draft) return;
    if (!draft.title?.trim()) {
      toast.error('عنوان الصفحة مطلوب');
      return;
    }
    setSaving(true);
    try {
      const saved = await savePage(slug, {
        title: draft.title,
        subtitle: draft.subtitle,
        icon: draft.icon,
        content: draft.content,
      });
      setPage(saved);
      writeCache(slug, saved);
      setDraft(null);
      toast.success('تم حفظ الصفحة ونشرها');
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر الحفظ، حاولي مرة ثانية');
    } finally {
      setSaving(false);
    }
  }, [draft, slug, savePage]);

  return useMemo(
    () => ({
      data: draft ?? page,
      failed: failed && !page,
      reload: load,
      canEdit,
      editing,
      dirty,
      saving,
      startEditing,
      cancel,
      save,
      update,
    }),
    [draft, page, failed, load, canEdit, editing, dirty, saving, startEditing, cancel, save, update]
  );
};

/** عمليات على قوائم (أقسام، أسئلة...) داخل المسودة */
export const listOps = (update, path) => ({
  move: (index, delta) =>
    update(path, (list = []) => {
      const next = [...list];
      const target = index + delta;
      if (target < 0 || target >= next.length) return next;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    }),
  remove: (index) => update(path, (list = []) => list.filter((_, i) => i !== index)),
  insert: (index, item) =>
    update(path, (list = []) => [...list.slice(0, index), item, ...list.slice(index)]),
});
